import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Checkbox,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Paper
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useCurrentUser } from '../utils/useCurrentUser';

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

const PermissionManager = ({ open, onClose, roleId, roleName }) => {
    const { empId, boId } = useCurrentUser();
    const [permissions, setPermissions] = useState([]);
    const [selectedPermissions, setSelectedPermissions] = useState({});

    // Define fixed column order
    const permissionOrder = ["View", "Edit", "Delete"];

    useEffect(() => {
        if (open && roleId) {
            fetchPermissions();
        }
    }, [open, roleId]);

    const fetchPermissions = async () => {
        try {
            const response = await fetch(`${BASE_URL}/api/roles/${boId}/${roleId}/permissions`);
            const data = await response.json();

            // Filter out deactivated permissions and sort for each screen
            const sorted = data.permissions.map(screen => ({
                ...screen,
                permissions: [...screen.permissions]
                    .filter(p => p.delete_flag !== "Y")
                    .sort((a, b) => permissionOrder.indexOf(a.name) - permissionOrder.indexOf(b.name))
            })).filter(screen => screen.permissions.length > 0);

            setPermissions(sorted);
            // Build selectedPermissions object from "assigned"
            const initialSelected = {};
            sorted.forEach(screen => {
                initialSelected[screen.screen_id] = {};
                screen.permissions.forEach(permission => {
                    initialSelected[screen.screen_id][permission.permission_id] = permission.assigned;
                });
            });

            setSelectedPermissions(initialSelected);
        } catch (error) {
            console.error('Error fetching permissions:', error);
        }
    };

    const handlePermissionToggle = (permissionId, screenId) => {
        setSelectedPermissions(prev => ({
            ...prev,
            [screenId]: {
                ...prev[screenId],
                [permissionId]: !prev[screenId]?.[permissionId]
            }
        }));
    };
    const savePermissions = async () => {
        try {
            // Convert selectedPermissions into an array of objects
            const payload = [];
            Object.entries(selectedPermissions).forEach(([screenId, perms]) => {
                Object.entries(perms).forEach(([permissionId, isAssigned]) => {
                    payload.push({
                        screen_id: Number(screenId),
                        permission_id: Number(permissionId),
                        assigned: isAssigned,
                        empId
                    });
                });
            });

            // Send to backend
            const res = await fetch(`${BASE_URL}/api/roles/${boId}/${roleId}/permissions`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ permissions: payload })
            });
            if (!res.ok) throw new Error("Failed to save");
            // Refresh state from backend
            await fetchPermissions();
            // Now safe to close
            onClose();
        } catch (error) {
            console.error("Error saving permissions:", error);
        }
    };
    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                Manage Screen Permissions - {roleName}
                <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent>
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell><b>Screen</b></TableCell>
                                {permissionOrder.map((p) => (
                                    <TableCell key={p} align="center"><b>{p}</b></TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {permissions.map((screen) => (
                                <TableRow key={screen.screen_id}>
                                    <TableCell>{screen.name}</TableCell>
                                    {screen.permissions.map((permission) => (
                                        <TableCell key={permission.permission_id} align="center">
                                            <Checkbox
                                                checked={selectedPermissions[screen.screen_id]?.[permission.permission_id] || false}
                                                onChange={() => handlePermissionToggle(permission.permission_id, screen.screen_id)}
                                            />
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button onClick={savePermissions} variant="contained">Save</Button>
            </DialogActions>
        </Dialog>
    );
};

export default PermissionManager;
